import React, { useMemo } from "react";
import {
  Container,
  Card,
  Button,
  Row,
  Col,
  Badge,
  Alert,
  Spinner,
} from "react-bootstrap";
import { periodoActual } from "./utils";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { checkPermission } from "../../functions/checkPermission";
import {
  FaBalanceScale,
  FaBook,
  FaChartLine,
  FaClipboardList,
  FaMoneyCheckAlt,
  FaNetworkWired,
  FaProjectDiagram,
  FaReceipt,
  FaDatabase,
  FaFileInvoiceDollar,
} from "react-icons/fa";
import GestionCompras from "./components/GestionCompras";
import GestionCuentasPagar from "./components/GestionCuentasPagar";
import GestionCuentasCobrar from "./components/GestionCuentasCobrar";
import IntegracionPlanillasContable from "./components/IntegracionPlanillasContable";
import GestionTesoreriaModulo from "../GestionTesoreria";
import GestionVentasModulo from "../GestionVentas";

import PlanCuentasPCGE from "./components/PlanCuentasPCGE";
import LibrosContables from "./components/LibrosContables";
import ReportesFinancieros from "./components/ReportesFinancieros";
import IntegracionSIRE from "./components/IntegracionSIRE";

// Nuevos componentes del Centro Financiero
import LibroDiario from "./components/LibroDiario";
import LibroMayor from "./components/LibroMayor";
import BalanceComprobacion from "./components/BalanceComprobacion";
import EstadosFinancieros from "./components/EstadosFinancieros";
import AnalisisContable from "./components/AnalisisContable";
import CierresContables from "./components/CierresContables";

const MODULOS_BASE = [
  {
    key: "diario",
    titulo: "Libro Diario",
    icono: <FaBook />,
    objetivo: "Registro cronológico de todas las operaciones comerciales del ledger gco_asientos.",
    items: [
      "Filtros avanzados y paginación",
      "Exportación a Excel y TXT PLE 5.1",
      "Trazabilidad de asientos",
    ],
  },
  {
    key: "mayor",
    titulo: "Libro Mayor",
    icono: <FaBook />,
    objetivo: "Saldos acumulados y movimientos detallados clasificados por cuenta contable.",
    items: [
      "Running balance por naturaleza",
      "Filtros por cuenta y rango de fechas",
      "Exportación a Excel oficial",
    ],
  },
  {
    key: "balance-comprobacion",
    titulo: "Balance Comprobación",
    icono: <FaBalanceScale />,
    objetivo: "Verificación de sumas del Debe, Haber y saldos deudores/acreedores.",
    items: [
      "Validación de cuadre automático",
      "Saldos acumulados del PCGE",
      "Indicador visual de descuadres",
    ],
  },
  {
    key: "estados-financieros",
    titulo: "Estados Financieros",
    icono: <FaChartLine />,
    objetivo: "Estado de Resultados (P&L) y Situación Financiera oficial de la empresa.",
    items: [
      "Ecuación fundamental Activo = Pasivo + Patrimonio",
      "Resultado operativo y neto",
      "Indicadores financieros reales",
    ],
  },
  {
    key: "analisis-contable",
    titulo: "Análisis Contable",
    icono: <FaNetworkWired />,
    objetivo: "Trazabilidad E2E desde un asiento/CUO hasta el documento operativo original.",
    items: [
      "Búsqueda por CUO o ID de Asiento",
      "Trazabilidad Ventas, Compras, Tesorería",
      "Enlace a documentos de origen",
    ],
  },
  {
    key: "cierres",
    titulo: "Cierres Contables",
    icono: <FaClipboardList />,
    objetivo: "Bloqueo y control de periodos fiscales cerrados para auditoría SUNAT.",
    items: [
      "Evaluación pre-cierre de salud",
      "Bloqueo definitivo de periodos",
      "Reapertura controlada por rol",
    ],
  },
  {
    key: "compras",
    titulo: "Registro de Facturas",
    icono: <FaFileInvoiceDollar />,
    objetivo: "Recepción de comprobantes, validación SUNAT y vinculación con OC.",
    items: [
      "Importación desde OC certificada",
      "Validación XML/CDR (SUNAT)",
      "Clasificación de Gasto/Costo",
      "Registro de detracciones y retenciones",
    ],
  },
  {
    key: "pagos",
    titulo: "Cuentas por Pagar (OC)",
    icono: <FaMoneyCheckAlt />,
    objetivo: "Programación de pagos y control de deuda a proveedores amarrada a OC.",
    items: [
      "Calendario de vencimientos",
      "Registro de pagos parciales/totales",
      "Diferencia de cambio automática",
      "Reporte de antigüedad de deuda",
    ],
  },
  {
    key: "cobros",
    titulo: "Cuentas por Cobrar (OS)",
    icono: <FaMoneyCheckAlt />,
    objetivo: "Seguimiento de saldos por cobrar amarrados a Ordenes de Venta (OS).",
    items: [
      "CxC por orden de venta",
      "Control facturado vs cobrado",
      "Estado de cobranza por OS",
      "Trazabilidad OS ↔ facturas",
    ],
  },
  {
    key: "tesoreria",
    titulo: "Tesorería (Pagos y Cobros)",
    icono: <FaMoneyCheckAlt />,
    objetivo: "Registro de caja y bancos con amarre automático a documento contable.",
    items: [
      "Registro de pagos y cobros",
      "Asiento contable automático por movimiento",
      "Trazabilidad movimiento ↔ asiento",
      "Control multiempresa por RUC",
    ],
  },
  {
    key: "ventas",
    titulo: "Ventas (OV, Factura y Boleta)",
    icono: <FaFileInvoiceDollar />,
    objetivo: "Gestión comercial de orden de venta y comprobantes con asiento automático.",
    items: [
      "Registro de ordenes de venta",
      "Emisión de factura y boleta",
      "Asiento contable automático",
      "Trazabilidad documental completa",
    ],
  },
  {
    key: "planillas",

    titulo: "Integración con Planillas",
    icono: <FaProjectDiagram />,
    objetivo: "Asientos automáticos por naturaleza y destino desde nóminas.",
    items: [
      "Asiento de naturaleza (clase 62)",
      "Asiento de destino (94/95 por centro de costos)",
      "Provisiones mensuales (CTS, gratificación, vacaciones)",
      "Trazabilidad planilla → asiento",
    ],
  },
  {
    key: "pcge",
    titulo: "Plan de Cuentas (PCGE)",
    icono: <FaBook />,
    objetivo: "Catálogo contable configurable y escalable por empresa.",
    items: [
      "Estructura PCGE por niveles",
      "Subcuentas personalizadas",
      "Reglas de amarre 6x → 9x",
      "Control de vigencia y estado de cuentas",
    ],
  },
  {
    key: "libros",
    titulo: "Libros Electrónicos",
    icono: <FaClipboardList />,
    objetivo: "Base para RVIE/RCE y conciliación con SIRE.",
    items: [
      "Libro Diario digital",
      "Libro Mayor digital",
      "Estructuras comparables con propuesta SUNAT",
      "Bitácora de envíos y diferencias",
    ],
  },
  {
    key: "reportes",
    titulo: "Reportes Financieros",
    icono: <FaChartLine />,
    objetivo: "Visibilidad financiera operativa en tiempo real.",
    items: [
      "Estado de Resultados (P&L)",
      "Balance General",
      "Flujo de Caja proyectado vs real",
      "KPIs financieros por unidad de negocio",
    ],
  },
];

const GestionContable = ({ usuario, seccion: propSeccion = "dashboard" }) => {
  const { seccion: urlSeccion } = useParams();
  const seccion = urlSeccion || propSeccion || "dashboard";
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const selectedCuo = queryParams.get("cuo") || "";
  const [kpis, setKpis] = React.useState({ ingresos: 0, gastos: 0, utilidad: 0, activos: 0, pasivos: 0, patrimonio: 0 });
  const [loadingKpis, setLoadingKpis] = React.useState(false);
  const rucEmpresa = usuario?.ruc || "";

  React.useEffect(() => {
    const fetchKpis = async () => {
      if (!rucEmpresa) return;
      try {
        setLoadingKpis(true);
        const { obtenerReporteFinanciero } = await import("../../services/contabilidad/reportesService");
        const res = await obtenerReporteFinanciero(rucEmpresa, periodoActual());
        setKpis({
          ingresos: res.pnl?.ingresos || 0,
          gastos: res.pnl?.gastos || 0,
          utilidad: res.pnl?.utilidad || 0,
          activos: res.balance?.activos || 0,
          pasivos: res.balance?.pasivos || 0,
          patrimonio: res.balance?.patrimonio || 0,
        });
      } catch (e) {
        console.error("Error loading dashboard KPIs:", e);
      } finally {
        setLoadingKpis(false);
      }
    };
    fetchKpis();
  }, [rucEmpresa]);

  const handleSelectCuo = (cuo) => {
    navigate(`/contabilidad/analisis-contable?cuo=${cuo}`);
  };

  const tienePermisoContabilidad = (proceso) => {
    const p = String(proceso || "").toUpperCase();
    if (checkPermission(usuario, "contabilidad")) {
      return true;
    }
    if (p === "COBROS") {
      return checkPermission(usuario, "contabilidad", "COBROS") || checkPermission(usuario, "contabilidad", "VENTAS");
    }
    return checkPermission(usuario, "contabilidad", p);
  };

  // Módulos dinámicos basados en permisos
  const modulosPermitidos = MODULOS_BASE.filter(m => 
    tienePermisoContabilidad(m.key.toUpperCase()) || checkPermission(usuario, "contabilidad")
  );

  const resumen = useMemo(() => ({
    totalModulos: modulosPermitidos.length,
    totalEntregables: modulosPermitidos.reduce((acc, m) => acc + m.items.length, 0),
    estado: "Operativo",
  }), [modulosPermitidos]);

  const renderContenido = () => {
    // Protección de acceso directo por URL
    if (seccion !== "dashboard") {
      const tienePermiso = tienePermisoContabilidad(seccion.toUpperCase());
      if (!tienePermiso) {
        return (
          <Alert variant="danger" className="border-0 shadow-sm p-4">
            <h5 className="fw-bold">Acceso Denegado</h5>
            Usted no tiene permisos para acceder a la sección: <strong>{seccion.toUpperCase()}</strong>.
          </Alert>
        );
      }
    }

    switch (seccion) {
      case "diario":
        return <LibroDiario usuario={usuario} onSelectCuo={handleSelectCuo} />;
      case "mayor":
        return <LibroMayor usuario={usuario} />;
      case "balance-comprobacion":
        return <BalanceComprobacion usuario={usuario} />;
      case "estados-financieros":
        return <EstadosFinancieros usuario={usuario} />;
      case "analisis-contable":
        return <AnalisisContable usuario={usuario} initialCuo={selectedCuo} />;
      case "cierres":
        return <CierresContables usuario={usuario} />;
      case "compras":
        return <GestionCompras usuario={usuario} />;
      case "pagos":
        return <GestionCuentasPagar usuario={usuario} />;
      case "cobros":
        return <GestionCuentasCobrar usuario={usuario} />;
      case "tesoreria":
        return <GestionTesoreriaModulo usuario={usuario} mostrarTabsRuta={false} seccionInicial="pagos" />;
      case "ventas":
        return <GestionVentasModulo usuario={usuario} mostrarTabsRuta={false} seccionInicial="ordenes" modoContabilidad />;
      case "planillas":
        return <IntegracionPlanillasContable usuario={usuario} />;
      case "pcge":
        return <PlanCuentasPCGE usuario={usuario} />;
      case "libros":
        return (
          <div>
            <LibrosContables usuario={usuario} />
            <div className="my-5 py-3 text-center">
              <span className="px-4 py-1 rounded-pill small fw-semibold text-muted"
                style={{ background: "#f3f4f6", letterSpacing: "2px", border: "1px solid #e5e7eb" }}>
                INTEGRACIONES SUNAT
              </span>
            </div>
            <h5 className="fw-bold mb-4 d-flex align-items-center text-dark">
              <FaNetworkWired className="me-2 text-primary" />
              Integración SIRE (RVIE / RCE)
            </h5>
            <IntegracionSIRE usuario={usuario} />
          </div>
        );
      case "reportes":
        return <ReportesFinancieros usuario={usuario} />;

      default:
        return (
          <>
            {/* Barra superior */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1} className="text-center">
                    <div className="rounded-3 p-3 d-inline-block"
                      style={{ background: "linear-gradient(135deg, #4a4a4a 0%, #6b6b6b 100%)", color: "white" }}>
                      <FaBalanceScale size={24} />
                    </div>
                  </Col>
                  <Col md={7}>
                    <h5 className="mb-1 fw-semibold">Centro Financiero Unificado</h5>
                    <p className="text-muted mb-0 small">
                      Consolidado financiero en tiempo real alimentado directamente desde el ledger contable `gco_asientos`.
                    </p>
                  </Col>
                  <Col md={4} className="text-md-end mt-3 mt-md-0">
                    <Badge
                      className="px-3 py-2"
                      style={{
                        background: "rgba(107,114,128,0.1)",
                        color: "#374151",
                        border: "1px solid rgba(107,114,128,0.25)",
                        borderRadius: "8px",
                        fontSize: "0.85rem",
                      }}
                    >
                      Período Actual: {periodoActual()}
                    </Badge>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* KPIs Financieros Reales */}
            <h6 className="fw-bold text-muted mb-3 text-uppercase" style={{ letterSpacing: "1px" }}>KPIs de Rendimiento y Balance</h6>
            {loadingKpis ? (
              <div className="text-center py-4"><Spinner animation="border" variant="secondary" /></div>
            ) : (
              <Row className="mb-4 g-3">
                <Col md={4}>
                  <Card className="border-0 shadow-sm text-center py-4" style={{ borderRadius: "15px", background: "linear-gradient(135deg, #059669 0%, #10b981 100%)", color: "white" }}>
                    <h6 className="opacity-75 small mb-1 text-uppercase">Ingresos Totales</h6>
                    <h3 className="fw-bold mb-0">S/ {kpis.ingresos.toFixed(2)}</h3>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="border-0 shadow-sm text-center py-4" style={{ borderRadius: "15px", background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)", color: "white" }}>
                    <h6 className="opacity-75 small mb-1 text-uppercase">Gastos Totales</h6>
                    <h3 className="fw-bold mb-0">S/ {kpis.gastos.toFixed(2)}</h3>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="border-0 shadow-sm text-center py-4" style={{ borderRadius: "15px", background: "linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)", color: "white" }}>
                    <h6 className="opacity-75 small mb-1 text-uppercase">Resultado del Ejercicio</h6>
                    <h3 className="fw-bold mb-0">S/ {kpis.utilidad.toFixed(2)}</h3>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="border-0 shadow-sm text-center py-3 bg-white" style={{ borderRadius: "15px" }}>
                    <h6 className="text-muted small mb-1">Activos Totales</h6>
                    <h4 className="fw-bold text-success mb-0">S/ {kpis.activos.toFixed(2)}</h4>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="border-0 shadow-sm text-center py-3 bg-white" style={{ borderRadius: "15px" }}>
                    <h6 className="text-muted small mb-1">Pasivos Totales</h6>
                    <h4 className="fw-bold text-danger mb-0">S/ {kpis.pasivos.toFixed(2)}</h4>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="border-0 shadow-sm text-center py-3 bg-white" style={{ borderRadius: "15px" }}>
                    <h6 className="text-muted small mb-1">Patrimonio Neto</h6>
                    <h4 className="fw-bold text-primary mb-0">S/ {kpis.patrimonio.toFixed(2)}</h4>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Grid de módulos */}
            <h6 className="fw-bold text-muted mb-3 text-uppercase" style={{ letterSpacing: "1px" }}>Acceso Directo a Módulos</h6>
            <Row className="g-4">
              {modulosPermitidos.map((m) => (
                <Col md={6} lg={4} key={m.key}>
                  <Link
                    to={`/contabilidad/${m.key}`}
                    className="text-decoration-none h-100 d-block"
                  >
                    <Card className="h-100 border-0 shadow-sm cont-card-hover" style={{ borderRadius: "15px", transition: "all 0.3s ease" }}>
                      <Card.Body className="p-4 d-flex flex-column">
                        <div className="d-flex align-items-center mb-3">
                          <div className="rounded-3 p-3 d-flex align-items-center justify-content-center me-3"
                            style={{ background: "linear-gradient(135deg, #4a4a4a 0%, #6b6b6b 100%)", color: "white", width: "50px", height: "50px" }}>
                            {m.icono}
                          </div>
                          <h6 className="fw-bold mb-0 text-dark">{m.titulo}</h6>
                        </div>
                        <p className="text-muted small flex-grow-1 mb-3">
                          {m.objetivo}
                        </p>
                        <ul className="list-unstyled mb-0 border-top pt-3">
                          {m.items.slice(0, 2).map((item, idx) => (
                            <li key={idx} className="text-muted small mb-1 d-flex align-items-start gap-2">
                              <span className="text-primary mt-1" style={{ fontSize: "0.5rem" }}>●</span>
                              {item}
                            </li>
                          ))}
                          {m.items.length > 2 && (
                            <li className="text-muted small opacity-50 italic">+ {m.items.length - 2} funciones más...</li>
                          )}
                        </ul>
                      </Card.Body>
                    </Card>
                  </Link>
                </Col>
              ))}
            </Row>
          </>
        );

    }
  };

  const currentModule = MODULOS_BASE.find((m) => m.key === seccion) || {
    titulo: "Gestión Contable",
    icono: <FaBalanceScale />,
    objetivo: "Ecosistema financiero integrado para operaciones escalables en Perú",
  };

  return (
    <div
      className="min-vh-100"
      style={{ background: "#ffffff", minHeight: "100vh", width: "100vw", maxWidth: "100%", padding: "20px 0" }}
    >
      <Container fluid style={{ width: "95%", maxWidth: "100%" }}>
        <Card className="shadow-lg border-0" style={{ borderRadius: "15px", overflow: "hidden", minHeight: "90vh" }}>
          <Card.Header
            className="text-white py-4"
            style={{
              background: "linear-gradient(135deg, #3a3a3a 0%, #5c5c5c 100%)",
              borderBottom: "3px solid #9ca3af",
            }}
          >
            <div className="text-center">
              <h2 className="fw-bold mb-1 d-flex align-items-center justify-content-center gap-2">
                <span style={{ opacity: 0.9 }}>{currentModule.icono}</span>
                {currentModule.titulo}
              </h2>
              <p className="mb-0 opacity-75 small">{currentModule.objetivo}</p>
            </div>
          </Card.Header>
          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {renderContenido()}
          </Card.Body>
        </Card>
      </Container>

      <style>{`
        .cont-row-hover:hover {
          background-color: #f9fafb !important;
          cursor: pointer;
        }
        .cont-row-hover {
          transition: background-color 0.15s ease;
        }
      `}</style>
    </div>
  );
};

export default GestionContable;
